import { useQuery } from '@tanstack/react-query'
import { PartnerService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { useAuth } from './useAuth'
import { toast } from 'sonner'
import { useTar } from '@/modules/i18n'

export const assignmentsQueryKeys = {
  all: () => ['assignments'] as const,
  lists: () => ['assignments', 'list'] as const,
  list: (assignmentIds?: string[]) => ['assignments', 'list', assignmentIds] as const,
  details: () => ['assignments', 'detail'] as const,
  detail: (assignmentId: string) => ['assignments', 'detail', assignmentId] as const,
}

const fetchAssignment = async (assignmentId: string): Promise<PartnerService.Assignment> => {
  const response = await apis.partnerService.assignmentsAssignmentIdGet(assignmentId)
  return response.data.data
}

const fetchAssignments = async (assignmentIds: string[]): Promise<Record<string, PartnerService.Assignment>> => {
  if (!assignmentIds || assignmentIds.length === 0) {
    return {}
  }

  // Fetch all assignments in parallel
  const assignments = await Promise.all(
    assignmentIds.map(id => 
      fetchAssignment(id).catch(error => {
        console.error(`Failed to fetch assignment ${id}:`, error)
        return null
      })
    )
  )

  // Build a map of assignmentId -> Assignment
  const assignmentsMap: Record<string, PartnerService.Assignment> = {}
  assignments.forEach((assignment, index) => {
    if (assignment) {
      assignmentsMap[assignmentIds[index]] = assignment
    }
  })

  return assignmentsMap
}

export const useAssignments = (assignmentIds?: string[]) => {
  const { isPartner, isAdmin } = useAuth()
  const { t } = useTar()
  const enabled = (isPartner || isAdmin) && !!assignmentIds && assignmentIds.length > 0
  
  const query = useQuery({
    queryKey: assignmentsQueryKeys.list(assignmentIds),
    queryFn: async () => {
      if (!assignmentIds || assignmentIds.length === 0) {
        return Promise.resolve({})
      }
      try {
        return await fetchAssignments(assignmentIds)
      } catch (error) {
        console.error('Failed to fetch assignments:', error)
        toast.error(t('errors.failedToFetchAssignments'))
        return {}
      }
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
  })

  return {
    assignments: (query.data ?? {}) as Record<string, PartnerService.Assignment>,
    isLoading: query.isPending && enabled,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  } as const
}
